import { useState, useEffect } from "react";
import {
  Form, Input, Button, Typography, Divider, Select, InputNumber, message, Space
} from "antd";
import {
  UserAddOutlined, MailOutlined, LockOutlined, ApiOutlined,
  EyeOutlined, EyeInvisibleOutlined, BankOutlined, HomeOutlined, UserOutlined
} from "@ant-design/icons";
import { API_BASE } from "./config";
import { useNavigate } from "react-router-dom";
import { useThemeContext } from "./context/ThemeContext";
import coreItLogo from "./assets/CoreIT.png";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const Register = () => {
  const isDark = true;
  const [loading, setLoading] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/CoreCountries/all`)
      .then((res) => res.json())
      .then((data) =>
        setCountryOptions((data || []).map((c) => ({ label: c.name, value: c.id })))
      )
      .catch(() => message.error("Ülkeler yüklenemedi"));

    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!selectedCountry) {
      setCityOptions([]);
      return;
    }

    fetch(`${API_BASE}/CoreCities/by-country/${selectedCountry}`)
      .then((res) => res.json())
      .then((data) =>
        setCityOptions((data || []).map((c) => ({ label: c.name, value: c.id })))
      )
      .catch(() => message.error("Şehirler yüklenemedi"));
  }, [selectedCountry]);

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const loginEndpoint = `${API_BASE}/Firms/register`;
      const res = await fetch(loginEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      setLoading(false);

      if (res.ok) {
        message.success("Kayıt işlemi tamamlandı! Giriş sayfasına yönlendiriliyorsunuz...", 2);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      } else {
        const err = await res.json();
        message.error(err.message || "Kayıt sırasında hata oluştu!");
      }
    } catch {
      setLoading(false);
      message.error("Sunucuya bağlanılamadı!");
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(135deg, #0f0f1e 0%, #1a1a2e 50%, #16213e 100%)"
          : "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        padding: "20px",
        transition: "background 0.5s ease",
      }}
    >
      {/* Animated background elements */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          opacity: 0.1,
          background: isDark
            ? "radial-gradient(circle at 20% 50%, #667eea 0%, transparent 50%), radial-gradient(circle at 80% 50%, #f093fb 0%, transparent 50%)"
            : "radial-gradient(circle at 30% 50%, rgba(255,255,255,0.2) 0%, transparent 50%)",
        }}
      />

      <div
        style={{
          maxWidth: "1200px",
          width: isMobile ? "95%" : "100%",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          boxShadow: isDark
            ? "0 20px 60px rgba(0, 0, 0, 0.6)"
            : "0 20px 60px rgba(0, 0, 0, 0.2)",
          borderRadius: isMobile ? "16px" : "24px",
          overflow: "hidden",
          backgroundColor: isDark ? "#1e1e2f" : "#ffffff",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left side - Brand/Visual Panel */}
        <div
          style={{
            flex: isMobile ? "none" : "0 0 500px",
            width: isMobile ? "100%" : "auto",
            background: isDark
              ? "linear-gradient(135deg, #2d3561 0%, #1f2544 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            padding: isMobile ? "40px 20px" : "60px 40px",
            display: isMobile ? "none" : "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            color: "#fff",
            position: "relative",
          }}
        >
          {/* Decorative circles */}
          <div
            style={{
              position: "absolute",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.05)",
              top: "-150px",
              left: "-150px",
            }}
          />
          <div
            style={{
              position: "absolute",
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.05)",
              bottom: "-100px",
              right: "-100px",
            }}
          />

          <div style={{ position: "relative", zIndex: 2, textAlign: "center" }}>
            {/* Logo */}
            <div
              style={{
                marginBottom: "30px",
                animation: "fadeInDown 1s ease-out",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
              }}
            >
              <img
                src={coreItLogo}
                alt="Core IT Logo"
                style={{
                  width: "200px",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                  margin: "0 auto",
                  filter: isDark ? "brightness(1.2)" : "brightness(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.15))",
                }}
              />
            </div>

            <Title
              level={2}
              style={{
                color: "#fff",
                fontWeight: 800,
                fontSize: "36px",
                marginBottom: "16px",
                letterSpacing: "-1px",
                textShadow: "0 2px 10px rgba(0,0,0,0.2)",
              }}
            >
              Core Optica
            </Title>

            <Paragraph
              style={{
                color: "rgba(255,255,255,0.9)",
                fontSize: "16px",
                marginBottom: "40px",
                lineHeight: "1.6",
                maxWidth: "350px",
              }}
            >
              Yapay zeka destekli <b>kurumsal platform</b> ile optik işletmenizi dijitalleştirin.
            </Paragraph>

            {/* Feature badges */}
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
              <Space style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                <ApiOutlined style={{ fontSize: "16px" }} />
                <span>Bulut Tabanlı Sistem</span>
              </Space>
              <Space style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px" }}>
                <UserAddOutlined style={{ fontSize: "16px" }} />
                <span>Kolay Kurulum</span>
              </Space>
            </div>

            <Divider style={{ borderColor: "rgba(255,255,255,0.2)", margin: "30px 0 20px" }} />

            <Text
              style={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "12px",
                display: "block",
              }}
            >
              © 2024 Core IT Bilişim A.Ş.
              <br />
              Tüm hakları saklıdır.
            </Text>
          </div>
        </div>

        {/* Right side - Register Form */}
        <div
          style={{
            flex: "1",
            padding: isMobile ? "30px 20px" : "30px 35px",
            backgroundColor: isDark ? "#1e1e2f" : "#ffffff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            minHeight: isMobile ? "100vh" : "auto",
          }}
        >
          <div style={{ maxWidth: "500px", margin: "0 auto", width: "100%" }}>
            {/* Welcome text */}
            <div style={{ marginBottom: isMobile ? "15px" : "20px" }}>
              <Title
                level={2}
                style={{
                  color: isDark ? "#fff" : "#1a1a2e",
                  fontWeight: 700,
                  fontSize: isMobile ? "22px" : "26px",
                  marginBottom: "6px",
                }}
              >
                Kayıt Olun
              </Title>
              <Text
                style={{
                  color: isDark ? "rgba(255,255,255,0.6)" : "#666",
                  fontSize: "14px",
                }}
              >
                Hesabınızı oluşturun ve hemen başlayın
              </Text>
            </div>

            <Form
              layout="vertical"
              onFinish={handleRegister}
              autoComplete="off"
              name="register-form"
            >
              {/* 2 kolonlu grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <Form.Item
                  name="firmName"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Firma Adı</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    prefix={<BankOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />}
                    placeholder="Firma adı"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="taxNumber"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Vergi No</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    placeholder="Vergi numarası"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="taxOffice"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Vergi Dairesi</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    placeholder="Vergi dairesi"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="address"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Adres</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    prefix={<HomeOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />}
                    placeholder="Firma adresi"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="countryId"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Ülke</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Select
                    showSearch
                    placeholder="Ülke seçin"
                    onChange={setSelectedCountry}
                    className={isDark ? "dark-select" : "light-select"}
                  >
                    {countryOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="cityId"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Şehir</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Select
                    showSearch
                    placeholder="Önce ülke seçin"
                    disabled={!selectedCountry}
                    className={isDark ? "dark-select" : "light-select"}
                  >
                    {cityOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item
                  name="adminFullName"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Ad Soyad</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />}
                    placeholder="Ad soyad"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="adminEmail"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>E-posta</span>}
                  rules={[
                    { required: true, message: "Gerekli" },
                    { type: "email", message: "Geçersiz" }
                  ]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />}
                    placeholder="E-posta"
                    autoComplete="new-email"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="adminPassword"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Şifre</span>}
                  rules={[
                    { required: true, message: "Gerekli" },
                    { min: 6, message: "Min 6 karakter" }
                  ]}
                  style={{ marginBottom: "4px" }}
                >
                  <Input.Password
                    prefix={<LockOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />}
                    iconRender={(visible) =>
                      visible ? (
                        <EyeOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />
                      ) : (
                        <EyeInvisibleOutlined style={{ color: isDark ? "#667eea" : "#764ba2", fontSize: "13px" }} />
                      )
                    }
                    placeholder="Şifre"
                    autoComplete="new-password"
                    style={{
                      borderRadius: "8px",
                      padding: "7px 11px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                      color: isDark ? "#fff" : "#333",
                    }}
                  />
                </Form.Item>

                <Form.Item
                  name="package"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Paket</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <Select
                    placeholder="Paket seçin"
                    className={isDark ? "dark-select" : "light-select"}
                  >
                    <Option value="basic">Temel</Option>
                    <Option value="pro">Profesyonel</Option>
                    <Option value="enterprise">Kurumsal</Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="maxBranches"
                  label={<span style={{ color: isDark ? "rgba(255,255,255,0.85)" : "#333", fontWeight: 500, fontSize: "13px" }}>Şube Sayısı</span>}
                  rules={[{ required: true, message: "Gerekli" }]}
                  style={{ marginBottom: "4px" }}
                >
                  <InputNumber
                    min={1}
                    placeholder="Şube sayısı"
                    className={isDark ? "dark-input-number" : "light-input-number"}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      fontSize: "13px",
                      backgroundColor: isDark ? "#2a2a3e" : "#f8f9fa",
                      border: isDark ? "1px solid #3a3a4e" : "1px solid #e0e0e0",
                    }}
                  />
                </Form.Item>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                style={{
                  background: isDark
                    ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  borderRadius: "10px",
                  height: "42px",
                  fontSize: "15px",
                  fontWeight: 600,
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                  marginTop: "12px",
                  marginBottom: "12px",
                }}
              >
                Kayıt Ol
              </Button>

              <div style={{ textAlign: "center" }}>
                <Text style={{ color: isDark ? "rgba(255,255,255,0.6)" : "#999", fontSize: "13px" }}>
                  Zaten hesabınız var mı?{" "}
                </Text>
                <Button
                  type="link"
                  style={{
                    color: isDark ? "#667eea" : "#764ba2",
                    fontSize: "13px",
                    padding: "0 4px",
                    fontWeight: 600,
                  }}
                  onClick={() => navigate("/login")}
                >
                  Giriş Yap
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Add keyframe animations and autocomplete fix */}
      <style>{`
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Remove autocomplete white background */
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${isDark ? '#2a2a3e' : '#f8f9fa'} inset !important;
          -webkit-text-fill-color: ${isDark ? '#fff' : '#333'} !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        input[type="password"]:-webkit-autofill,
        input[type="password"]:-webkit-autofill:hover,
        input[type="password"]:-webkit-autofill:focus,
        input[type="password"]:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 30px ${isDark ? '#2a2a3e' : '#f8f9fa'} inset !important;
          -webkit-text-fill-color: ${isDark ? '#fff' : '#333'} !important;
          transition: background-color 5000s ease-in-out 0s;
        }

        /* InputNumber text color fix and height */
        .dark-input-number,
        .light-input-number {
          height: 36px !important;
        }

        .dark-input-number input,
        .light-input-number input {
          height: 34px !important;
        }

        .dark-input-number input {
          color: #fff !important;
        }

        .light-input-number input {
          color: #333 !important;
        }

        .dark-input-number .ant-input-number-handler-wrap {
          background-color: #2a2a3e !important;
        }

        .light-input-number .ant-input-number-handler-wrap {
          background-color: #f8f9fa !important;
        }

        /* All form inputs same height */
        .ant-form-item .ant-input {
          height: 36px !important;
          line-height: 36px !important;
          padding: 7px 11px !important;
        }

        .ant-form-item .ant-input-password {
          height: 36px !important;
        }

        .ant-form-item .ant-input-password input {
          height: 34px !important;
          padding: 7px 11px !important;
        }

        .ant-form-item .ant-input-affix-wrapper {
          height: 36px !important;
          padding: 7px 11px !important;
        }

        .ant-form-item .ant-input-affix-wrapper input {
          height: 20px !important;
        }

        /* Select (ComboBox) styling */
        .dark-select .ant-select-selector {
          background-color: #2a2a3e !important;
          border: 1px solid #3a3a4e !important;
          color: #fff !important;
          border-radius: 8px !important;
          padding: 7px 11px !important;
          font-size: 13px !important;
          height: 36px !important;
          min-height: 36px !important;
        }

        .light-select .ant-select-selector {
          background-color: #f8f9fa !important;
          border: 1px solid #e0e0e0 !important;
          color: #333 !important;
          border-radius: 8px !important;
          padding: 7px 11px !important;
          font-size: 13px !important;
          height: 36px !important;
          min-height: 36px !important;
        }

        /* Select height fix */
        .dark-select .ant-select-single .ant-select-selector,
        .light-select .ant-select-single .ant-select-selector {
          display: flex !important;
          align-items: center !important;
        }

        .dark-select .ant-select-selection-search,
        .light-select .ant-select-selection-search {
          display: flex !important;
          align-items: center !important;
        }

        .dark-select .ant-select-selection-search input {
          color: #fff !important;
        }

        .light-select .ant-select-selection-search input {
          color: #333 !important;
        }

        .dark-select .ant-select-selection-item {
          color: #fff !important;
        }

        .light-select .ant-select-selection-item {
          color: #333 !important;
        }

        .dark-select .ant-select-selection-placeholder {
          color: rgba(255, 255, 255, 0.48) !important;
        }

        .light-select .ant-select-selection-placeholder {
          color: rgba(0, 0, 0, 0.48) !important;
        }

        /* Input placeholder colors - balanced visibility */
        input::placeholder,
        .ant-input::placeholder,
        .ant-input-number input::placeholder,
        .ant-input-password input::placeholder {
          color: ${isDark ? 'rgba(255, 255, 255, 0.48)' : 'rgba(0, 0, 0, 0.48)'} !important;
          opacity: 1 !important;
        }

        /* Force placeholder color for all form inputs */
        .ant-form-item input::placeholder {
          color: ${isDark ? 'rgba(255, 255, 255, 0.48)' : 'rgba(0, 0, 0, 0.48)'} !important;
          opacity: 1 !important;
        }

        /* Input number specific */
        .dark-input-number input::placeholder {
          color: rgba(255, 255, 255, 0.48) !important;
        }

        .light-input-number input::placeholder {
          color: rgba(0, 0, 0, 0.48) !important;
        }

        .dark-select .ant-select-arrow {
          color: #667eea !important;
        }

        .light-select .ant-select-arrow {
          color: #764ba2 !important;
        }

        /* Dropdown menu styling */
        .ant-select-dropdown {
          background-color: ${isDark ? '#2a2a3e' : '#fff'} !important;
        }

        .ant-select-item {
          color: ${isDark ? '#fff' : '#333'} !important;
        }

        .ant-select-item-option-selected {
          background-color: ${isDark ? '#3a3a4e' : '#f0f0f0'} !important;
        }

        .ant-select-item-option-active {
          background-color: ${isDark ? '#3a3a4e' : '#f5f5f5'} !important;
        }
      `}</style>
    </div>
  );
};

export default Register;
